import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Mail, ArrowLeft, CheckCircle2 } from 'lucide-react';
import Logo from '../components/Logo';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
      toast.success('If this email is registered, a reset link has been sent.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset link');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md p-8 shadow-xl text-center">
          <CheckCircle2 className="w-16 h-16 text-[#009639] mx-auto mb-4" />
          <h1 data-testid="reset-email-sent-title" className="text-2xl font-bold mb-3">Check Your Email</h1>
          <p className="text-gray-600 mb-2">
            If <span className="font-medium">{email}</span> is registered, we've sent a password reset link.
          </p>
          <p className="text-sm text-gray-500 mb-6">
            The link expires in 30 minutes. Check your spam folder if you don't see it.
          </p>
          <div className="space-y-3">
            <Button
              data-testid="resend-reset-btn"
              variant="outline"
              className="w-full"
              onClick={() => { setSent(false); }}
            >
              Didn't receive it? Try again
            </Button>
            <Link to="/login" className="block">
              <Button variant="ghost" className="w-full text-gray-600">
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back to Login
              </Button>
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-8 shadow-xl">
        <div className="text-center mb-8">
          <Logo className="mx-auto mb-4 justify-center" />
          <Mail className="w-16 h-16 text-[#CE1126] mx-auto mb-4" />
          <h1 className="text-3xl font-bold mb-2">Forgot Password?</h1>
          <p className="text-gray-600">Enter your email and we'll send you a reset link</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="email">Email Address</Label>
            <Input
              id="email"
              type="email"
              placeholder="your@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="forgot-password-email-input"
            />
          </div>

          <Button
            type="submit"
            className="w-full btn-uae-red"
            disabled={loading}
            data-testid="forgot-password-submit-btn"
          >
            {loading ? 'Sending...' : 'Send Reset Link'}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <Link to="/login" className="text-sm text-gray-600 hover:text-[#CE1126] inline-flex items-center">
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to Login
          </Link>
        </div>
      </Card>
    </div>
  );
}
